import { FC, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useBandContext } from "Bands/contexts";
import {
  dispatchGetLastGuestlistSent,
  eventBlockSelectorCreator,
  lastGuestlistSent,
} from "Bands/state";
import {
  EventBlocks,
  IGuestlistBlock,
  IGuestlistBlockStatus,
  IGuestlistBlockType,
} from "Bands/types";
import { useBandRegionalSettings } from "Bands/utils";
import { copy } from "shared/assets";
import { List, Message, Title } from "shared/components";
import { Spacer } from "shared/layout";
import { MutedText } from "shared/styles";
import { IStatus } from "shared/types";
import { formatDateTime, mapUserPermissions } from "shared/utils";
import { NotFlexSpacer } from "../EventSchedule/styles";
import { CreateGuest } from "./CreateGuest";
import { GuestOverview } from "./GuestOverview";
import {
  StyledGuestlistForm,
  StyledGuestListTicketAndPassesWrapper,
} from "./style";

const viewCopy = copy.guestlistBlock.view;

interface EventGuestlistBlockProps {
  blockId: string;
  showActions?: boolean;
}

export const EventGuestlistBlock: FC<EventGuestlistBlockProps> = ({
  blockId,
  showActions,
}) => {
  const dispatch = useDispatch();
  const { findBandUser, ticketAndPassTypes } = useBandContext();
  const { dateFormat, timeFormat } = useBandRegionalSettings();

  const guestlistBlockSelector = eventBlockSelectorCreator(
    blockId,
    EventBlocks.guestlist
  );
  const guestlistBlock = useSelector(guestlistBlockSelector) as IGuestlistBlock;

  const exportSelector = useMemo(
    () =>
      lastGuestlistSent(
        guestlistBlock.lastMailedDetailsExport &&
          guestlistBlock.lastMailedDetailsExport.id
      ),
    [guestlistBlock.lastMailedDetailsExport]
  );
  const lastGuestlist = useSelector(exportSelector);

  const userCanAddGuests = useMemo(
    () => mapUserPermissions(guestlistBlock?.permissions || []).ADD_GUEST,
    [guestlistBlock]
  );

  const ticketAndPassesData = useMemo(
    () =>
      guestlistBlock.ticketAndPassTypes
        .map(({ ticketAndPassType, ...rest }) => ({
          ...rest,
          ticketAndPassType: ticketAndPassType.id,
        }))
        .sort((a, b) => {
          const aIndex = ticketAndPassTypes.findIndex(
            (type) => type.id === a.ticketAndPassType
          );
          const bIndex = ticketAndPassTypes.findIndex(
            (type) => type.id === b.ticketAndPassType
          );

          return aIndex - bIndex;
        }),
    [guestlistBlock.ticketAndPassTypes, ticketAndPassTypes]
  );

  const formattedSentAt = useMemo(() => {
    if (!lastGuestlist) return;
    const dateTime = formatDateTime(lastGuestlist.sentAt, {
      dateFormat,
      timeFormat,
    })!;

    const bandUser = findBandUser(lastGuestlist.createdBy.id);

    return viewCopy.messages.sentAt
      .replace("%dt", dateTime)
      .replace(
        "%u",
        bandUser ? `${bandUser.firstname!} ${bandUser.lastname}` : "unknown"
      );
  }, [lastGuestlist, dateFormat, timeFormat, findBandUser]);

  useEffect(() => {
    if (guestlistBlock.lastMailedDetailsExport?.id) {
      dispatchGetLastGuestlistSent(
        dispatch,
        guestlistBlock.lastMailedDetailsExport.id
      );
    }
  }, [dispatch, guestlistBlock.lastMailedDetailsExport?.id]);

  if (!guestlistBlock) return null;

  return (
    <NotFlexSpacer size="l">
      {(lastGuestlist ||
        guestlistBlock.status === IGuestlistBlockStatus.closed) && (
        <Message>
          <List>
            {guestlistBlock.status === IGuestlistBlockStatus.closed && (
              <li>{viewCopy.messages.closed}</li>
            )}
            {lastGuestlist && lastGuestlist.sentAt && (
              <li>{formattedSentAt}</li>
            )}
          </List>
        </Message>
      )}
      <StyledGuestlistForm>
        {userCanAddGuests &&
          guestlistBlock.status === IGuestlistBlockStatus.open && (
            <CreateGuest block={guestlistBlock} />
          )}
        {!userCanAddGuests &&
          guestlistBlock.status === IGuestlistBlockStatus.open &&
          guestlistBlock.guestBlockType === IGuestlistBlockType.private && (
            <Message
              status={IStatus.warning}
              message={viewCopy.messages.noPermission}
            />
          )}
      </StyledGuestlistForm>

      <GuestOverview block={guestlistBlock} showActions={showActions} />

      {ticketAndPassesData.length !== 0 && (
        <StyledGuestListTicketAndPassesWrapper size="m">
          <Spacer size="l">
            {ticketAndPassesData.map((data) => (
              <Spacer size="xxs" spaceDirection="horizontal" key={data.id}>
                <Title size="s" title={data.label} asComponent="h4" />{" "}
                <MutedText>
                  {data.isUnlimited
                    ? "unlimited"
                    : `${data.ticketsAvailable}/${data.ticketsTotal} tickets available`}
                </MutedText>
              </Spacer>
            ))}
          </Spacer>
        </StyledGuestListTicketAndPassesWrapper>
      )}
    </NotFlexSpacer>
  );
};
